<template>
  <section class="text-image-section">
    <div class="container text-image-grid" :class="{ reversed: imagePosition === 'left' }">
      <div class="text-side">
        <h2 v-if="title" class="section-title">{{ title }}</h2>
        <p v-if="description" class="section-desc">{{ description }}</p>
        <ul v-if="bullets?.length" class="bullet-list">
          <li v-for="(b, i) in bullets" :key="i">
            <span class="bullet-check">&#10003;</span>
            <span>{{ b }}</span>
          </li>
        </ul>
        <NuxtLink v-if="ctaText" :to="ctaLink || '#'" class="section-cta">
          {{ ctaText }}
        </NuxtLink>
      </div>
      <div class="image-side">
        <img v-if="imageUrl" :src="proxyUrl(imageUrl)" alt="" class="section-image" />
        <div v-else class="image-placeholder"></div>
      </div>
    </div>
  </section>
</template>

<script setup>
defineProps({
  title: String,
  description: String,
  bullets: Array,
  ctaText: String,
  ctaLink: String,
  imageUrl: String,
  imagePosition: { type: String, default: 'right' }
})

function proxyUrl(url) {
  if (!url) return ''
  if (url.startsWith('http')) return `/api/assets/serve?url=${encodeURIComponent(url)}`
  return url
}
</script>

<style scoped>
/* Mobile-first. Ladder: 480 / 640 / 768 / 1024 / 1280 (variables.css). */
.text-image-section {
  padding: clamp(56px, 8vw, 100px) 0;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.text-image-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 40px;
  align-items: center;
}

@media (min-width: 768px) {
  .text-image-grid {
    grid-template-columns: 1fr 1fr;
  }

  .text-image-grid.reversed { direction: rtl; }
  .text-image-grid.reversed > * { direction: ltr; }
}

@media (min-width: 1024px) {
  .text-image-grid {
    gap: 60px;
  }
}

.section-title {
  font-family: var(--font-heading);
  font-size: clamp(1.7rem, 1.2rem + 2vw, 2.5rem);
  font-weight: 800;
  color: var(--text-main);
  margin-bottom: 16px;
  line-height: 1.2;
  text-wrap: balance;
}

.section-desc {
  color: var(--text-muted);
  font-size: 1.1rem;
  line-height: 1.7;
  margin-bottom: 24px;
}

.bullet-list {
  list-style: none;
  padding: 0;
  margin-bottom: 32px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.bullet-list li {
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--text-muted);
  font-size: 1rem;
}

.bullet-check {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(0, 242, 255, 0.1);
  color: var(--primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  flex-shrink: 0;
}

.section-cta {
  display: inline-block;
  min-height: 44px;
  padding: 12px 28px;
  background: var(--primary);
  color: #030304;
  font-weight: 700;
  border-radius: var(--radius-md);
  text-decoration: none;
  transition: all 0.2s;
}

.section-cta:hover {
  box-shadow: 0 0 20px var(--primary-glow);
  transform: translateY(-2px);
}

.section-image {
  width: 100%;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
}

.image-placeholder {
  width: 100%;
  aspect-ratio: 4/3;
  background: var(--surface-color);
  border: 1px dashed var(--border);
  border-radius: var(--radius-lg);
}

</style>
